import { SystemStyleObject } from '@chakra-ui/react'

import { ColorScheme } from '../../types'

/**
 * Grace period before an un-hovered tooltip closes. It exists only to cover
 * the 6px gap between crumb and tooltip while the pointer crosses it, so it is
 * deliberately near-imperceptible: the tooltip sits directly in the pointer's
 * exit path, and anything longer reads as the tooltip refusing to leave.
 */
export const TOOLTIP_CLOSE_DELAY_MS = 60

export const containerStyles: SystemStyleObject = {
  alignItems: 'center',
  display: 'flex',
  gap: '8px',
  px: '16px',
  py: '12px',
}

export const itemStyles = (
  colorScheme: ColorScheme,
  { isLink, isClickable }: { isLink: boolean; isClickable: boolean }
): SystemStyleObject => {
  const prefix = colorScheme === 'light' ? 'light.' : ''
  return {
    // Link crumbs read as links; static crumbs (incl. click-to-copy names) read
    // as primary text — mirroring the incident-drawer DarkHeader treatment.
    color: isLink ? `${prefix}text.link` : `${prefix}text.primary`,
    cursor: isClickable ? 'pointer' : 'default',
    fontFamily: "'Soehne', sans-serif",
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '16px',
    whiteSpace: 'nowrap',
    background: 'transparent',
    border: 'none',
    padding: 0,
  }
}

export const copyButtonStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => {
  const prefix = colorScheme === 'light' ? 'light.' : ''
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    padding: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    color: `${prefix}icon.secondary`,
    // The copy button keeps a transparent background, so hover must emphasise
    // toward the surface's primary text color (dark in light mode, light in
    // dark mode) — not icon.primary, which is white and would vanish on the
    // light drawer surface.
    _hover: { color: `${prefix}text.primary` },
    // Keyboard users reveal this button via focus, so it needs a visible ring.
    _focusVisible: {
      outline: '2px solid',
      outlineColor: `${prefix}interactive.focus-ring`,
      outlineOffset: '2px',
      color: `${prefix}text.primary`,
    },
  }
}

export const dividerStyles = (colorScheme: ColorScheme): SystemStyleObject => {
  const prefix = colorScheme === 'light' ? 'light.' : ''
  return {
    bg: `${prefix}border.subtle`,
    flexShrink: 0,
    // 20px matches the incident-drawer DarkHeader divider height.
    height: '20px',
    width: '1px',
  }
}

/**
 * The whole tooltip body is the copy target, not just the icon — a 12px icon
 * is a needlessly small thing to ask an operator to hit.
 */
export const copyTooltipContentStyles: SystemStyleObject = {
  alignItems: 'center',
  background: 'none',
  border: 'none',
  color: 'inherit',
  cursor: 'pointer',
  display: 'flex',
  font: 'inherit',
  gap: '8px',
  letterSpacing: 'inherit',
  p: 0,
  textAlign: 'left',
  textTransform: 'inherit',
}

export const copyIconStyles = (
  colorScheme: ColorScheme,
  hasCopied: boolean
): SystemStyleObject => {
  const prefix = colorScheme === 'light' ? 'light.' : ''
  return {
    alignItems: 'center',
    // Green while the copy is still being confirmed, matching the incident
    // drawer header's copy-link affordance.
    color: hasCopied ? `${prefix}status.resolved-text` : 'currentColor',
    display: 'flex',
    flexShrink: 0,
  }
}
